import type { SupabaseClient } from "@supabase/supabase-js";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppHeader } from "../components/AppHeader";
import { AuthDebugPanel } from "../components/AuthDebugPanel";
import { PageShell } from "../components/PageShell";
import { SplashScreen } from "../components/SplashScreen";
import { Card } from "../components/ui/Card";
import { logAuth } from "../lib/auth/debug-log";

// StrictMode (dev) monte le composant 2× et ré-exécute l'effet. Le code PKCE est
// à usage unique : le 1er échange le consomme, un 2e échouerait (« code verifier
// not found ») et afficherait une fausse erreur. On mémoïse la promesse par code
// → les deux montages partagent l'UNIQUE échange réel (et son résultat).
const exchangeByCode = new Map<
  string,
  ReturnType<SupabaseClient["auth"]["exchangeCodeForSession"]>
>();

type CallbackStatus = "exchanging" | "error";

interface AuthCallbackProps {
  client: SupabaseClient;
}

export function AuthCallback({ client }: AuthCallbackProps) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<CallbackStatus>("exchanging");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const search = window.location.search;
    const hash = window.location.hash;
    logAuth("info", "AuthCallback mount", { search, hash, href: window.location.href });

    // Supabase OAuth peut retourner les params en query (?code=) ou en hash
    // (#code=) selon le flow. On accepte les deux.
    const params = new URLSearchParams(search);
    const hashParams = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const code = params.get("code") ?? hashParams.get("code");
    const errorCode = params.get("error_code") ?? hashParams.get("error_code");

    // Cas "identité Google déjà liée" : ce n'est pas vraiment une erreur,
    // l'identité EST liée. On retourne à l'accueil ; l'auto-reconnect (unlink
    // + relink) de Home prendra le relais pour récupérer un provider_token.
    if (errorCode === "identity_already_exists") {
      logAuth("info", "AuthCallback : identity_already_exists → navigate /");
      navigate("/", { replace: true });
      return;
    }

    if (!code) {
      logAuth("warn", "AuthCallback : pas de code dans l'URL", { search, hash });
      setStatus("error");
      setErrorMessage("Lien de connexion invalide ou expiré");
      return;
    }

    logAuth("info", "AuthCallback → exchangeCodeForSession", { code: `${code.slice(0, 12)}...` });
    let cancelled = false;
    const pending = exchangeByCode.get(code) ?? client.auth.exchangeCodeForSession(code);
    exchangeByCode.set(code, pending);
    pending.then(({ error }) => {
      if (cancelled) return;
      if (error) {
        logAuth("warn", "exchangeCodeForSession error", error.message);
        setStatus("error");
        setErrorMessage(error.message);
      } else {
        logAuth("info", "exchangeCodeForSession ok → navigate /");
        navigate("/", { replace: true });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [client, navigate]);

  if (status === "error") {
    return (
      <PageShell>
        {/* Hero — cohérent avec l'écran de connexion */}
        <AppHeader variant="plain" title="Connexion" subtitle="La connexion n'a pas pu aboutir." />

        <Card variant="danger">
          <p role="alert" className="m-0">
            {errorMessage}
          </p>
        </Card>
        <AuthDebugPanel />
      </PageShell>
    );
  }
  return <SplashScreen />;
}
